import React, { useRef, useState, useCallback } from 'react';
import { useRequestSignUpCode, useSignUp } from '../../../hooks/userHooks';
import { USER_QUERY_KEYS, SERVER_ERROR_CODES, STRING_CONSTANTS } from '../../../constants/constants';
import { useQueryClient } from '@tanstack/react-query';
import { UniversalProps } from '../../../typesAndInterfaces/componentProps';
import { SIGNED_IN_STATUS, useSignedInContext } from '../../../context/SignedInContext';
import { useFocusEffect } from '@react-navigation/native';
import SignupUI from './SignupUI';
import { MB_accessTokenUtils } from '@/utils/general/access-token-utils';
import { MB_passwordUtils, MB_PASSWORD_UTIL_ERROR_TYPES } from '@/utils/general/password-utils';
import { MB_TextInputToolTipPortalRef } from '@/components/general/text-input';

const SignUp = ({ navigation }: UniversalProps) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [signUpCode, setSignUpCode] = useState('');
  const [isAskingForSignUpCode, setIsAskingForSignUpCode] = useState(false);
  const queryClient = useQueryClient();
  const { setSignedInStatus } = useSignedInContext();

  useFocusEffect(
    useCallback(() => {
      return () => {
        if (isAskingForSignUpCode) {
          setIsAskingForSignUpCode(false);
          setFirstName('');
          setLastName('');
          setEmail('');
          setPassword('');
          setConfirmPassword('');
        }
      };
    }, [isAskingForSignUpCode])
  );

  const generatePasswordErrorArray = (errorArray: MB_PASSWORD_UTIL_ERROR_TYPES[], doPasswordsMatch: boolean): { message: string; isError: boolean }[] => {
    const errors: { message: string; isError: boolean }[] = [];

    errors.push({
      message: MB_PASSWORD_UTIL_ERROR_TYPES.min,
      isError: errorArray.includes(MB_PASSWORD_UTIL_ERROR_TYPES.min) || errorArray.includes(MB_PASSWORD_UTIL_ERROR_TYPES.max),
    });
    errors.push({ message: MB_PASSWORD_UTIL_ERROR_TYPES.lowercase, isError: errorArray.includes(MB_PASSWORD_UTIL_ERROR_TYPES.lowercase) });
    errors.push({ message: MB_PASSWORD_UTIL_ERROR_TYPES.uppercase, isError: errorArray.includes(MB_PASSWORD_UTIL_ERROR_TYPES.uppercase) });
    errors.push({ message: MB_PASSWORD_UTIL_ERROR_TYPES.digits, isError: errorArray.includes(MB_PASSWORD_UTIL_ERROR_TYPES.digits) });
    errors.push({ message: MB_PASSWORD_UTIL_ERROR_TYPES.spaces, isError: errorArray.includes(MB_PASSWORD_UTIL_ERROR_TYPES.spaces) });
    if (errorArray.includes(MB_PASSWORD_UTIL_ERROR_TYPES.unknown)) {
      errors.push({ message: MB_PASSWORD_UTIL_ERROR_TYPES.unknown, isError: true });
    }

    errors.push({ message: MB_PASSWORD_UTIL_ERROR_TYPES.passwordMismatch, isError: !doPasswordsMatch });

    return errors;
  };

  const [passwordErrorArray, setPasswordErrorArray] = useState<{ message: string; isError: boolean }[]>(
    generatePasswordErrorArray(MB_passwordUtils.validatePassword('').errorArray, true)
  );

  const signUp = useSignUp();
  const requestSignUpCode = useRequestSignUpCode();

  const makeSignUpRequest = () => {
    if (!validateFields) {
      return;
    }

    if (!signUpCode) {
      setErrorMessage('Please enter your register code');
      return false;
    }

    signUp.mutate(
      {
        email,
        firstName: firstName,
        lastName: lastName,
        password,
        signUpCode: signUpCode.toUpperCase(),
      },
      {
        onSuccess: async data => {
          // Await the token write before enabling authed queries (see Login.tsx).
          await MB_accessTokenUtils.setAccessToken(data);
          setSignedInStatus(SIGNED_IN_STATUS.signedIn);
          queryClient.invalidateQueries([USER_QUERY_KEYS.getCurrentUsertData]);
        },
        onError: error => {
          if (error.errorCode === SERVER_ERROR_CODES.EMAIL_ALREADY_IN_USE) {
            setErrorMessage(STRING_CONSTANTS.EMAIL_ALREADY_IN_USE);
            return;
          } else if (error.errorCode === SERVER_ERROR_CODES.INVALID_SIGN_UP_CODE) {
            setErrorMessage(STRING_CONSTANTS.INVALID_SIGN_UP_CODE);
            return;
          } else if (error.errorCode === SERVER_ERROR_CODES.SIGN_UP_CODE_EXPIRED) {
            setErrorMessage(STRING_CONSTANTS.EXPIRED_SIGN_UP_CODE);
            return;
          }

          setErrorMessage('Failed to sign up, please try again');
        },
      }
    );
  };

  const validateFields = () => {
    if (!firstName) {
      setErrorMessage('First Name is a required field');
      return false;
    } else if (!lastName) {
      setErrorMessage('Last Name is a required field');
      return false;
    } else if (!email) {
      setErrorMessage('Email is a required field');
      return false;
    } else if (!password) {
      setErrorMessage('Password is a required field');
      return false;
    } else if (confirmPassword !== password) {
      setErrorMessage("Re-enter password doesn't match password");
      return false;
    }

    const validatePassword = MB_passwordUtils.validatePassword(password);

    if (validatePassword.errorMessage) {
      setErrorMessage(validatePassword.errorMessage);
      return false;
    }

    return true;
  };

  const attemptRequestRegistrationCode = (skipValidCheck?: boolean) => {
    if (!skipValidCheck && !validateFields()) {
      return;
    }

    requestSignUpCode.mutate(
      { email },
      {
        onSuccess: () => {
          setErrorMessage('');
          setIsAskingForSignUpCode(true);
        },
        onError: error => {
          if (error.errorCode === SERVER_ERROR_CODES.EMAIL_ALREADY_IN_USE) {
            setErrorMessage(STRING_CONSTANTS.EMAIL_ALREADY_IN_USE);
            return;
          }

          setErrorMessage(STRING_CONSTANTS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
        },
      }
    );
  };

  const onSubmitPressed = () => {
    if (isAskingForSignUpCode) {
      makeSignUpRequest();
    } else {
      attemptRequestRegistrationCode();
    }
  };

  const socialLoginCallback = (isSuccess: boolean) => {
    if (!isSuccess) {
      setErrorMessage(STRING_CONSTANTS.FAILED_TO_LOG_IN);
    }
  };

  const isSignUpButtonDisabled = () => {
    if (isAskingForSignUpCode && signUpCode) {
      return false;
    } else if (!isAskingForSignUpCode && email && firstName && lastName && password && confirmPassword) {
      return false;
    }

    return true;
  };

  const navigateToHome = () => navigation.navigate('HomeNavigator', { screen: 'GameNavigator', params: { screen: 'SelectGame' } });
  const navigateToLogin = () => navigation.navigate('Login');

  const portalRef = useRef<MB_TextInputToolTipPortalRef>(null);

  return (
    <SignupUI
      socialLoginCallback={socialLoginCallback}
      isAskingForSignUpCode={isAskingForSignUpCode}
      firstName={firstName}
      lastName={lastName}
      setFirstName={setFirstName}
      setLastName={setLastName}
      setErrorMessage={setErrorMessage}
      email={email}
      password={password}
      confirmPassword={confirmPassword}
      setEmail={setEmail}
      setPassword={setPassword}
      setConfirmPassword={setConfirmPassword}
      setPasswordErrorArray={setPasswordErrorArray}
      generatePasswordErrorArray={generatePasswordErrorArray}
      portalRef={portalRef}
      passwordErrorArray={passwordErrorArray}
      signUpCode={signUpCode}
      setSignUpCode={setSignUpCode}
      errorMessage={errorMessage}
      onSubmitPressed={onSubmitPressed}
      signUp={signUp}
      requestSignUpCode={requestSignUpCode}
      isSignUpButtonDisabled={isSignUpButtonDisabled}
      attemptRequestRegistrationCode={attemptRequestRegistrationCode}
      navigateToHome={navigateToHome}
      navigateToLogin={navigateToLogin}
    />
  );
};

export { SignUp };
